import React, { useState, useEffect } from 'react';
import {
  Box, Grid, Typography, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, Button, Alert, IconButton, Tooltip,
} from '@mui/material';
import { Refresh, Delete } from '@mui/icons-material';
import axios from 'axios';
import OrderEntry from './OrderEntry';
import PositionManager from './PositionManager';

export default function TradingTerminal({ apiUrl, user }) {
  const [positions, setPositions] = useState([]);
  const [orders, setOrders] = useState([]);
  const [portfolio, setPortfolio] = useState(null);
  const [loading, setLoading] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchTradingData();
    const interval = setInterval(fetchTradingData, 5000);
    return () => clearInterval(interval);
  }, []);

  const fetchTradingData = async () => {
    try {
      const [positionsRes, ordersRes, portfolioRes] = await Promise.all([
        axios.get(`${apiUrl}/orders/positions`, { headers }),
        axios.get(`${apiUrl}/orders/`, { headers }),
        axios.get(`${apiUrl}/orders/portfolio`, { headers }),
      ]);
      setPositions((positionsRes.data.items || []).filter((p) => p.status === 'open'));
      setOrders((ordersRes.data.items || []).slice(0, 20));
      setPortfolio(portfolioRes.data);
    } catch (err) { console.error(err); }
  };

  const deleteOrder = async (orderId) => {
    try {
      await axios.delete(`${apiUrl}/orders/${orderId}`, { headers });
      setOrders(prev => prev.filter(o => o.id !== orderId));
    } catch (err) { console.error(err); }
  };

  return (
    <Box data-testid="trading-terminal">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5">Trading Terminal</Typography>
        <Button variant="outlined" size="small" startIcon={<Refresh />}
          onClick={() => { setLoading(true); fetchTradingData().finally(() => setLoading(false)); }}
          disabled={loading}>Refresh</Button>
      </Box>

      {portfolio && (
        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          {[
            { label: 'Available Margin', value: `${portfolio.available_margin.toLocaleString()}` },
            { label: 'Used Margin', value: `${portfolio.used_margin.toLocaleString()}` },
            { label: 'Daily P&L', value: `${portfolio.daily_pnl.toFixed(2)}`, color: portfolio.daily_pnl >= 0 ? 'success.main' : 'error.main' },
            { label: 'Open Positions', value: positions.length },
          ].map((c, i) => (
            <Grid item xs={6} sm={3} key={i}>
              <Paper sx={{ p: 1.5, textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary">{c.label}</Typography>
                <Typography variant="h6" color={c.color || 'text.primary'}>{c.value}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <OrderEntry apiUrl={apiUrl} onOrderPlaced={fetchTradingData} />
        </Grid>
        <Grid item xs={12} md={8}>
          <PositionManager positions={positions} apiUrl={apiUrl} onUpdate={fetchTradingData} />
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>Recent Orders</Typography>
            <TableContainer sx={{ maxHeight: 300 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Time</TableCell>
                    <TableCell>Strategy</TableCell>
                    <TableCell>Symbol</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell align="right">Qty</TableCell>
                    <TableCell align="right">Price</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Mode</TableCell>
                    <TableCell align="right">P&L</TableCell>
                    <TableCell align="center"></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {orders.map((order) => (
                    <TableRow key={order.id}>
                      <TableCell><Typography variant="caption">{new Date(order.created_at).toLocaleTimeString()}</Typography></TableCell>
                      <TableCell>
                        <Typography variant="caption" color="primary" fontWeight={600}>{order.strategy_name || '-'}</Typography>
                      </TableCell>
                      <TableCell>{order.symbol}</TableCell>
                      <TableCell>
                        <Chip label={order.order_type} size="small"
                          color={order.order_type === 'BUY' ? 'success' : 'error'} />
                      </TableCell>
                      <TableCell align="right">{order.quantity}</TableCell>
                      <TableCell align="right">{order.entry_price?.toFixed(2)}</TableCell>
                      <TableCell><Chip label={order.status} size="small" /></TableCell>
                      <TableCell>
                        <Chip label={order.trade_mode?.toUpperCase()} size="small"
                          color={order.trade_mode === 'paper' ? 'default' : 'primary'} />
                      </TableCell>
                      <TableCell align="right" sx={{ color: (order.profit_loss || 0) >= 0 ? 'success.main' : 'error.main' }}>
                        {order.profit_loss ? `${order.profit_loss.toFixed(2)}` : '-'}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Delete order">
                          <IconButton size="small" color="error" onClick={() => deleteOrder(order.id)}
                            data-testid={`delete-order-${order.id}`}>
                            <Delete sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
